import React, { useState } from "react";
import { Section, Eyebrow, SectionTitle } from "@/components/Primitives";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! Our team will reach out shortly.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) { toast.error("Could not send message"); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <div className="eti-grid-bg border-b border-white/10 py-14">
        <Section>
          <Eyebrow>Contact & Locations</Eyebrow>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white leading-[1.05]">Let's Build Together</h1>
          <p className="mt-4 text-slate-300 max-w-2xl">Questions, partnerships or dispatch — our team is on call around the clock.</p>
        </Section>
      </div>

      <Section className="py-14 grid lg:grid-cols-2 gap-10">
        <div>
          <SectionTitle>Send Us A Message</SectionTitle>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-xs text-slate-300">Name</Label><Input value={form.name} onChange={set("name")} required className="bg-secondary border-white/10 mt-1" data-testid="contact-name-input" /></div>
              <div><Label className="text-xs text-slate-300">Email</Label><Input type="email" value={form.email} onChange={set("email")} required className="bg-secondary border-white/10 mt-1" data-testid="contact-email-input" /></div>
            </div>
            <div><Label className="text-xs text-slate-300">Subject</Label><Input value={form.subject} onChange={set("subject")} required className="bg-secondary border-white/10 mt-1" data-testid="contact-subject-input" /></div>
            <div><Label className="text-xs text-slate-300">Message</Label><Textarea value={form.message} onChange={set("message")} required rows={5} className="bg-secondary border-white/10 mt-1" data-testid="contact-message-input" /></div>
            <Button type="submit" disabled={loading} className="bg-primary hover:bg-primary/90 text-white font-semibold w-full" data-testid="contact-submit-button">{loading ? "Sending..." : "Send Message"}</Button>
          </form>
        </div>

        <div>
          <SectionTitle>Operational Hubs</SectionTitle>
          <div className="mt-6 space-y-4">
            {[
              [Phone, "24/7 Dispatch Hotline", "416-333-1755", "tel:+14163331755"],
              [Mail, "Email", "exclusivetradesinc@gmail.com", "mailto:exclusivetradesinc@gmail.com"],
              [MapPin, "Headquarters", "10 Belfield Rd, 2nd Floor, Etobicoke, Ontario, M9W 4J1"],
              [Clock, "Support Hours", "Round the clock, 365 days"],
            ].map(([Icon, t, d, href], i) => {
              const Wrap = href ? "a" : "div";
              return (
                <Wrap key={i} {...(href ? { href } : {})} className={`eti-card rounded-lg p-4 flex items-center gap-4 ${href ? "transition-colors hover:border-primary/60 group" : ""}`} data-testid={`contact-info-${i}`}>
                  <div className="h-10 w-10 rounded-md bg-primary/15 flex items-center justify-center"><Icon className="h-5 w-5 text-primary" /></div>
                  <div className="min-w-0"><p className="text-xs text-slate-400 uppercase tracking-wide">{t}</p><p className={`text-white font-medium break-words ${href ? "group-hover:text-primary transition-colors" : ""}`}>{d}</p></div>
                </Wrap>
              );
            })}
          </div>
          <div className="mt-6 rounded-xl overflow-hidden border border-white/10 h-64" data-testid="contact-map">
            <iframe title="Exclusive Trades Inc Location" width="100%" height="100%" style={{ border: 0, filter: "grayscale(0.4) invert(0.9) hue-rotate(180deg)" }} loading="lazy"
              src="https://www.google.com/maps?q=10+Belfield+Rd,+Etobicoke,+ON+M9W+4J1&output=embed" />
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Contact;
